import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getFeedback, postFeedback } from '../../DashBoard/Api';
import Loaders from '../../../../GlobalComponents/KenLoader';
import { FeedBackData } from '../../Interfaces/IDashBoard';
import Fields from './Fields';
import { makeStyles } from '@mui/styles';
import { Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import storageService from '../../../../Utils/storageService';

export interface IStartupDetailProps {
  startUpDetails: string | undefined;
  ref: any;
  setAvgScore: any;
  avgScore: any;
}

const useStyles = makeStyles(() => ({
  feedBackScrollContainer: {
    height: '320px',
    overflow: 'auto',
    padding: '10px 150px 10px 20px',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 600px)': {
      padding: '6px',
      height: '300px',
    },
    '@media (min-width: 1920px)': {
      height: '380px',
    },
  },
  evaluationSave: {
    fontSize: '12px !important',
    fontWeight: '500 !important',
    color: '#333333 !important',
    cursor: 'pointer',
    '@media (min-width: 1920px)': {
      fontSize: '16px !important',
    },
  },
  evaluationSubmit: {
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    '@media (min-width: 1920px)': {
      fontSize: '16px',
    },
  },
}));

const FeedBack: React.FunctionComponent<IStartupDetailProps> = forwardRef((props, ref) => {
  const classes = useStyles();
  const { avgScore, setAvgScore } = props;
  const { id } = useParams<{ id: string }>(); // Get application ID from URL
  const location: any = useLocation();
  const isViewMode = Boolean(location?.state?.isEvaluated);

  const [teamStrength, setTeamStrength] = useState<any>({});
  const [feedback, setfeedback] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [select, setSelect] = useState<any>();
  const [impPayload, setImpPayload] = useState<any>();
  const { enqueueSnackbar } = useSnackbar();
  const [evaluted, setEvaluated] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const navigate = useHistory();
  const [loader, setLoader] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [feedbackScore, setFeedbackScore] = useState<any>();

  const theme = useTheme();
  const isMobilescreen = useMediaQuery('(max-width:600px)');

  const [contactId, setContactId] = useState<string>('');
  const [applicationId, setApplicationId] = useState<string>('');
  const [round, setRound] = useState<string>('');
  const [panelId, setPanelId] = useState<string>('');

  // Load data from storage on component mount
  useEffect(() => {
    const loadStorageData = async () => {
      try {
        const contactIdData = await storageService.getItem('contactId');
        const applicationIdData = await storageService.getItem('applicationId'); 
        const roundData = await storageService.getItem('appStatus');
        const panelIdData = await storageService.getItem('PanelId');
        const userData = await storageService.getItem('user');
        
        // If contactId is not in storage, try to get it from user data
        const finalContactId = contactIdData || userData?.accountId || '';
        const finalApplicationId = applicationIdData || id || ''; // Use URL param as fallback
        
        setContactId(finalContactId);
        setApplicationId(finalApplicationId);
        setRound(roundData || '');
        setPanelId(panelIdData || '');
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    loadStorageData();
  }, []);

  useEffect(() => {
    // Make sure we have all required parameters before making API call
    if (contactId && applicationId) {
      // Use 'Shortlisted' as default round if not available
      const finalRound = round || 'Shortlisted';
      
      setLoader(true);
      
      getFeedback(contactId, applicationId, finalRound)
        .then((response) => {
          if (response) {
            setLoader(false);
            
            // Extract feedback data based on your API response structure
            const feedbackData = response?.data?.data?.feedback || [];
            const attachments = response?.data?.data?.attachments || [];
            const teamMembers = response?.data?.data?.teamMembers || [];
            const application = response?.data?.data?.application || {};
            
            setfeedback(feedbackData);
            setData({
              feedback: feedbackData,
              attachments: attachments,
              teamMembers: teamMembers,
              application: application
            });

            // Check if evaluation is already completed
            const isEvaluated = feedbackData?.some((section: any) => 
              section?.parameters?.some((param: any) => param?.questionResponse !== null)
            );
            
            if (isEvaluated) {
              setEvaluated(true);
            }
          }
        })
        .catch((err) => {
          console.error('API Error:', err);
          enqueueSnackbar('Something went wrong', { variant: 'error' });
          setLoader(false);
        });
    }
  }, [contactId, applicationId, round]);

  React.useEffect(() => {
    if (feedback) {
      setImpPayload(feedback);
    }
  }, [feedback]);

  let scoreId: any;
  if (feedback) {
    scoreId = feedback[0]?.scoreRecordId;
  } else {
    scoreId = data?.scoreRecordId;
  }

  React.useEffect(() => {
    setFeedbackScore(scoreId);
  }, [data]);

  useImperativeHandle(ref, () => ({
    handleSubmit: (type: string) => {
      handleSubmit(type);
    },
  }));

  const handleSubmit = (type: string) => {
    if (isViewMode) {
      enqueueSnackbar('This evaluation is in view mode and cannot be submitted again.', {
        variant: 'info',
      });
      return;
    }

    if (!impPayload?.length) {
      enqueueSnackbar('No evaluation data available to submit.', {
        variant: 'warning',
      });
      return;
    }

    if (type === 'submit' && evaluted) {
      enqueueSnackbar('Feedback has already been submitted for this application.', {
        variant: 'info',
      });
      return;
    }

    if (isSubmitting) {
      return;
    }

    let allFieldsAreMandatory = true;

    impPayload?.forEach((el: any) => {
      if (!el.parameters || el.parameters.some((elem: any) => elem.weightage !== 0 && (elem.questionResponse === null || elem.questionResponse === undefined))) {
        allFieldsAreMandatory = false;
        return;
      }
    });

    let payload = data?.scoreId
      ? {
          memberId: contactId,
          panelAssociationId: panelId,
          response: impPayload,
          evaluated: type === 'save' ? false : true,
          scoreRecordId: scoreId,
          avgScore: avgScore,
        }
      : {
          memberId: contactId,
          panelAssociationId: panelId,
          response: impPayload,
          evaluated: type === 'save' ? false : true,
          scoreRecordId: scoreId,
          avgScore: avgScore,
        };

    if (allFieldsAreMandatory && payload.evaluated) {
      // condition for submit button
      setIsSubmitting(true);
      postFeedback(payload)
        .then((res) => {
          if (res) {
            setEvaluated(true);
            enqueueSnackbar('The feedback has been posted for this application', {
              variant: 'success',
            });
            navigate.push(`/home`);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else if (payload.evaluated == false) {
      // condition for save button
      setIsSubmitting(true);
      postFeedback(payload)
        .then((res) => {
          if (res) {
            setData(res);
            setFeedbackScore(res);
            enqueueSnackbar('Evaluation score saved.', {
              variant: 'success',
            });
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      // Handle the case where not all fields are mandatory
      enqueueSnackbar('Please enter feedback for all the parameters', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const calculateAverageScore = () => {
      let totalScore = 0;
      let totalCount = 0;

      impPayload?.forEach((el: any) => {
        el.parameters?.forEach((param: any) => {
          if (param.questionResponse !== undefined && param.weightage !== 0) {
            if (/\d/.test(param.questionResponse)) {
              totalScore += (parseInt(param.questionResponse) * param.weightage) / 100;
              totalCount++;
            }
          }
        });
      });

      const averageScore = totalCount > 0 ? totalScore : 0;
      setAvgScore(averageScore.toFixed(2));
    };

    // Call the function when the component mounts
    calculateAverageScore();
  }, [impPayload, setAvgScore]);

  return (
    <>
      {loader && <Loaders />}
      <Box 
        component={'div'} 
        className={`w-100 ${classes.feedBackScrollContainer}`}
        sx={{
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid container spacing={2} width={'100%'} sx={{ flex: 1 }}>
          {impPayload?.length > 0 ? (
            impPayload.map((el: any, indexMain: number) => {
              return (
                <React.Fragment key={indexMain}>
                  <Grid item lg={6} xs={12} md={12} spacing={2} width={'100%'}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#333', fontSize: '16px', fontWeight: '600' }}>
                        {el.sectionLabel || `Section ${indexMain + 1}`}
                      </Typography>
                      {el?.parameters?.map((item: FeedBackData, indexParam: number) => {
                        return (
                          <Box key={`${indexMain}-${indexParam}`} sx={{ mb: 2 }}>
                            <Fields
                              type={item}
                              indexParam={indexParam}
                              indexMain={indexMain}
                              isMobilescreen={isMobilescreen}
                              teamStrength={teamStrength}
                              impPayload={impPayload}
                              setImpPayload={setImpPayload}
                              select={select}
                              evaluted={evaluted || isViewMode}
                              setAvgScore={setAvgScore}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                </React.Fragment>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" sx={{ fontSize: '14px', color: '#8C8E94' }}>
                  No Evaluation Data Available
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#999', mt: 1 }}>
                  {!contactId || !applicationId 
                    ? 'Missing required parameters (contactId or applicationId)' 
                    : 'No feedback data returned from API'
                  }
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      {!isMobilescreen && !isViewMode && impPayload?.length > 0 && (
        <Box borderTop={'1px solid #00000033'} component={'div'} className="d-flex justify-content-end py-3 px-4">
          {evaluted ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckIcon sx={{ color: '#388E3C' }} />}
              disabled
              sx={{ borderRadius: '8px', fontSize: '10px', fontWeight: '400', width: '65px', height: '35px', border: '1px solid #DBDBDB', color: '#008221' }}
            >
              Submitted
            </Button>
          ) : (
            <Box component={'div'} className="d-flex align-items-center gap-4">
              <Typography
                className={classes.evaluationSave}
                component={'h6'}
                onClick={() => {
                  if (!isSubmitting) {
                    handleSubmit('save');
                  }
                }}
                sx={{ opacity: isSubmitting ? 0.6 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
              >
                Save as draft
              </Typography>
              <Button
                variant="contained"
                size="small"
                className={classes.evaluationSubmit}
                disabled={isSubmitting}
                onClick={() => handleSubmit('submit')}
              >
                Submit
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );
});

export default FeedBack;
